/**
 * Llamadas al servidor de juego.
 *
 * Siempre bajo `/api`, relativo al propio dominio: en producción Traefik enruta ese prefijo al
 * servidor y se lo quita antes de entregarle la petición, y en desarrollo el proxy de Vite
 * reproduce lo mismo. Así el código de la SPA es idéntico en los dos entornos y no hay CORS.
 */

import {
  adminUsersResponseSchema,
  apiErrorSchema,
  createGameResponseSchema,
  gameSnapshotResponseSchema,
  sessionResponseSchema,
  type AdminUser,
  type ApiErrorKey,
  type BootstrapRequest,
  type CreateGameResponse,
  type SessionResponse,
  type UpdateUserRequest,
} from '@clica/shared';
import { supabase } from '../auth/supabase.js';

/** Error del protocolo, con la clave de traducción que el cliente debe mostrar. */
export class ApiRequestError extends Error {
  constructor(
    readonly key: ApiErrorKey | 'auth.error.generic',
    readonly status: number,
  ) {
    super(`La petición ha fallado con ${status}: ${key}`);
    this.name = 'ApiRequestError';
  }
}

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token === undefined ? {} : { Authorization: `Bearer ${token}` }),
      ...init.headers,
    },
  });

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(body);
    throw new ApiRequestError(
      parsed.success ? parsed.data.error.key : 'auth.error.generic',
      response.status,
    );
  }

  return body;
}

/**
 * Arranca la sesión: crea el perfil si es el primer acceso y devuelve el rol.
 *
 * El idioma detectado en el navegador viaja aquí porque el perfil lo crea el servidor. Si no se
 * enviara, todo perfil nuevo nacería con el idioma de reserva y la detección sería inútil: un
 * alumno con el móvil en catalán vería la interfaz en inglés.
 */
export async function bootstrapSession(body: BootstrapRequest): Promise<SessionResponse> {
  const data = await request('/session/bootstrap', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return sessionResponseSchema.parse(data);
}

export async function fetchAdminUsers(): Promise<readonly AdminUser[]> {
  const data = await request('/admin/users');
  return adminUsersResponseSchema.parse(data).users;
}

export async function updateAdminUser(id: string, body: UpdateUserRequest): Promise<AdminUser> {
  const data = await request(`/admin/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return adminUsersResponseSchema.shape.users.element.parse(data);
}

/**
 * Abre una sala a partir de un cuestionario.
 *
 * Es la única llamada HTTP del bucle de juego: todo lo demás viaja por el socket. El servidor
 * comprueba aquí que el cuestionario se puede jugar, así que un 409 no es un fallo de la SPA sino
 * la respuesta correcta a un cuestionario incompleto.
 */
export async function createGame(quizId: string): Promise<CreateGameResponse> {
  const data = await request('/games', {
    method: 'POST',
    body: JSON.stringify({ quizId }),
  });
  return createGameResponseSchema.parse(data);
}

/**
 * Elimina la cuenta (§16.2).
 *
 * **No borra las respuestas**: las desvincula. Se conservan las estadísticas agregadas de las
 * partidas —lo que da valor pedagógico— y desaparece la persona.
 */
/**
 * El cuestionario congelado de una partida, para el informe.
 *
 * Va por el servidor y no por PostgREST porque `games.quiz_snapshot` está revocado al rol
 * `authenticated` —dentro están las respuestas correctas—, y ese rol lo comparten la alumna y la
 * profesora: la columna no distingue, RLS solo decide filas. El informe no cargaba para nadie.
 */
export async function fetchGameSnapshot(gameId: string): Promise<unknown> {
  const data = await request(`/games/${gameId}/snapshot`);
  return gameSnapshotResponseSchema.parse(data).snapshot;
}

export async function deleteAccount(): Promise<void> {
  await request('/account', { method: 'DELETE' });
}
